import React, { useEffect, useMemo, useState } from 'react'
import { adminRequest } from '../api/client'
import { BackLink, DetailGrid, SummaryGrid, Timeline } from '../components/AdminCommon'
import { Card, EmptyState, PageTitle, SearchField, StatusBadge, formatDate } from '../components/Ui'

function targetRoute(item) {
  if (!item?.targetId) return null
  if (item.targetType === 'REVIEW') return `/content/reviews/${item.targetId}`
  if (item.targetType === 'PORTFOLIO') return `/content/portfolios/${item.targetId}`
  if (item.targetType === 'STORY') return `/content/stories/${item.targetId}`
  return null
}

export function ModerationCenter({ navigate, query }) {
  const initialOwner = query?.get('owner') || ''
  const [items, setItems] = useState([])
  const [search, setSearch] = useState(initialOwner)
  const [status, setStatus] = useState('ALL')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let disposed = false
    adminRequest('/api/v1/admin/moderation-cases')
      .then((response) => { if (!disposed) setItems(response?.data || []) })
      .catch((nextError) => { if (!disposed) setError(nextError?.message || '사후관리 케이스를 불러오지 못했습니다.') })
      .finally(() => { if (!disposed) setLoading(false) })
    return () => { disposed = true }
  }, [])

  const statuses = useMemo(() => [...new Set(items.map((item) => item.status).filter(Boolean))], [items])
  const rows = useMemo(() => items.filter((item) => {
    const haystack = [item.targetType,item.targetId,item.origin,item.status,item.reason].join(' ').toLowerCase()
    return (!search || haystack.includes(search.toLowerCase())) && (status === 'ALL' || item.status === status)
  }), [items, search, status])

  return <>
    <PageTitle eyebrow="UGC MODERATION · LIVE DB" title="블라인드·삭제 관리" description="moderation_cases에 실제 등록된 사후관리 케이스만 표시합니다." />
    <div className="policy-callout"><strong>예시 사후관리 사건 제거</strong><span>신고가 없는 공개 콘텐츠를 임의 케이스처럼 만들지 않습니다. 실제 moderation_cases가 비어 있으면 이 화면도 0건으로 표시됩니다.</span></div>
    <div className="metric-grid">
      <div className="metric-card metric-orange"><span>전체 케이스</span><strong>{items.length}</strong><small>moderation_cases</small></div>
      <div className="metric-card metric-red"><span>처리 중</span><strong>{items.filter((item) => !['RESOLVED','CLOSED','DELETED'].includes(item.status)).length}</strong><small>실제 현재 상태</small></div>
      <div className="metric-card metric-green"><span>종료</span><strong>{items.filter((item) => ['RESOLVED','CLOSED','DELETED'].includes(item.status)).length}</strong><small>실제 종료 케이스</small></div>
      <div className="metric-card metric-blue"><span>원장</span><strong>LIVE</strong><small>difan_dev</small></div>
    </div>
    <Card className="table-card">
      <div className="table-toolbar"><SearchField value={search} onChange={setSearch} placeholder="유형, 대상 ID, 사유 검색" /><div className="filter-buttons"><button className={status === 'ALL' ? 'active' : ''} onClick={() => setStatus('ALL')}>전체</button>{statuses.map((value) => <button key={value} className={status === value ? 'active' : ''} onClick={() => setStatus(value)}>{value}</button>)}</div></div>
      <div className="table-result-bar"><span>실제 케이스 <strong>{rows.length}</strong>건</span><small>source: moderation_cases</small></div>
      {loading ? <p className="help-copy">실제 사후관리 원장을 불러오는 중입니다.</p> : error ? <EmptyState title="사후관리 조회 실패" description={error} /> : rows.length ? <div className="table-scroll"><table className="data-table"><thead><tr><th>유형</th><th>대상 ID</th><th>발생 경로</th><th>사유</th><th>상태</th><th>생성일</th><th>수정일</th></tr></thead><tbody>{rows.map((item) => <tr className="clickable-row" key={item.id} onClick={() => navigate(`/content/moderation/${item.id}`)}><td><strong>{item.targetType}</strong></td><td><small className="mono">{item.targetId}</small></td><td>{item.origin}</td><td>{item.reason || '-'}</td><td><StatusBadge status={item.status} /></td><td>{formatDate(item.createdAt, true)}</td><td>{formatDate(item.updatedAt, true)}</td></tr>)}</tbody></table></div> : <EmptyState title="등록된 사후관리 케이스가 없습니다." description="현재 실제 신고·사후관리 원장이 비어 있습니다." />}
    </Card>
  </>
}

export function ModerationDetail({ id, navigate }) {
  const [item, setItem] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let disposed = false
    adminRequest(`/api/v1/admin/moderation-cases/${encodeURIComponent(id)}`)
      .then((response) => { if (!disposed) setItem(response?.data || null) })
      .catch((nextError) => { if (!disposed) setError(nextError?.message || '사후관리 케이스를 찾을 수 없습니다.') })
      .finally(() => { if (!disposed) setLoading(false) })
    return () => { disposed = true }
  }, [id])

  if (loading) return <><BackLink onClick={() => navigate('/content/moderation')} /><Card><p>실제 사후관리 원장을 조회하는 중입니다.</p></Card></>
  if (error || !item) return <><BackLink onClick={() => navigate('/content/moderation')} /><Card><EmptyState title="사후관리 케이스 조회 실패" description={error || '케이스를 찾을 수 없습니다.'} /></Card></>

  const relatedRoute = targetRoute(item)
  return <>
    <BackLink onClick={() => navigate('/content/moderation')} children="블라인드·삭제 관리로 돌아가기" />
    <PageTitle eyebrow="UGC MODERATION DETAIL · LIVE DB" title={item.id} description={`${item.targetType} · ${item.targetId}`} actions={relatedRoute ? <button className="button soft" onClick={() => navigate(relatedRoute)}>원본 관리 화면 보기</button> : null} />
    <SummaryGrid items={[
      { label: '상태', value: <StatusBadge status={item.status} /> },
      { label: '대상 유형', value: item.targetType },
      { label: '발생 경로', value: item.origin },
      { label: '수정일', value: formatDate(item.updatedAt, true) },
    ]} />
    <div className="dashboard-grid half"><Card title="케이스 정보"><DetailGrid items={[
      { label:'Case ID', value:item.id, wide:true },
      { label:'대상 ID', value:item.targetId, wide:true },
      { label:'대상 유형', value:item.targetType },
      { label:'발생 경로', value:item.origin },
      { label:'상태', value:item.status },
      { label:'생성일', value:formatDate(item.createdAt,true) },
      { label:'수정일', value:formatDate(item.updatedAt,true) },
    ]} /></Card><Card title="처리 사유"><div className="admin-long-text">{item.reason || '저장된 사유가 없습니다.'}</div></Card></div>
    <Card title="실제 처리 이력"><Timeline items={(item.history || []).map((history) => ({ label:history.action,detail:`${history.fromStatus || '-'} → ${history.toStatus || '-'}${history.reason ? ` · ${history.reason}` : ''}`,at:formatDate(history.createdAt,true) }))} />{!(item.history || []).length && <p className="help-copy">저장된 moderation_history가 없습니다.</p>}</Card>
    <Card title="처리 기능 상태"><p className="help-copy">현재 generic moderation case에 대한 서버 처리 명령 API는 별도로 제공되지 않으므로, 가짜 블라인드·복구 버튼을 만들지 않고 조회 전용으로 유지합니다. 실제 원본 후기 상태 변경은 후기 관리 화면의 서버 API를 사용합니다.</p></Card>
  </>
}
